import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { line, ONE_TIME_EXEMPTION_REASONS, Size, exemptionReasonLabel, fill, t, type NewPurchaseOrderRequest, type PurchaseExemptionFacts, type PurchaseFulfillmentMethodValue, type PurchaseOrderAnswer, type SuggestionRow, documentName, newDocumentTitle } from '@bms2/shared';
import { Button, DatePicker, Dropdown, Select } from '../../components/controls';
import { ItemTable, storedId, hasUnnamedDivider, newLine, savedRows, withDividers, type ItemLineDraft } from '../../components/composites/ItemTable';
import { ModalField } from '../../components/modals/ModalShell';
import { useDocumentToday } from '../../shell/ViewerZone';
import { CustomerNotesBox } from './CustomerNotesBox';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { ShipmentPreferencePicker } from './ShipmentPreferencePicker';
import { PURCHASE_ORDERS_PATH, apiFetch, post, postingAccountSearch, put, taxCategorySearch, useApi, useRows } from './api';
import { useItemPicker } from '../settings/api';
import { useAddNew } from './AddNewHost';

interface SalesOrderRow {
  id: string;
  salesOrderNumber: string;
  companyName: string;
  hasShipTo: boolean;
}

/** 🔨 A row the grid added carries no stored id; the save posts it as a new line rather than as an edit of one (decision 0445). */

/** The request the form posts from its drafts: only lines with an item and a quantity, priced in cents. */
export function requestFrom(input: { vendorCompanyId: string; method: PurchaseFulfillmentMethodValue; shipToSiteId: string; salesOrderId: string; dealId: string | null; documentDate?: string | null; deliveryDate?: string | null; shipmentPreferenceId?: string | null; customerNotes?: string; exemptionReason?: string | null; drafts: readonly ItemLineDraft[] }): NewPurchaseOrderRequest {
  // 🔒 The lines the save keeps, and the group dividers at their places among them (decision 0650).
  const { lines, dividers } = savedRows(
    input.drafts,
    (d) => Boolean(d.itemId || (d.freeText && d.itemLabel.trim())) && (d.qty ?? 0) > 0,
    // 🔨 The account and the category travel on the input, so a rewrite keeps what the line was written with (decisions 0097, 0435).
    // 🔨 The unit travels too, or the grid's closed-list override is discarded in silence (decision 0439).
    (d) => ({ id: storedId(d), itemId: d.itemId, description: d.freeText ? d.itemLabel.trim() : d.description || null, quantity: d.qty ?? 0, unitId: d.unitId ?? null, unitPriceCents: d.rateCents, accountId: d.accountId ?? null, taxCategoryId: d.taxCategoryId ?? null }),
  );
  return {
    vendorCompanyId: input.vendorCompanyId,
    fulfillmentMethod: input.method,
    shipToSiteId: input.method === 'Stock' ? input.shipToSiteId || null : null,
    salesOrderId: input.method === 'DropShip' ? input.salesOrderId || null : null,
    dealId: input.dealId,
    documentDate: input.documentDate || null,
    deliveryDate: input.deliveryDate || null,
    shipmentPreferenceId: input.shipmentPreferenceId || null,
    ...(input.customerNotes === undefined ? {} : { customerNotes: input.customerNotes }),
    // 🔒 Decision 9600: the one-time form's reason, or null for None; absent keeps what the order holds.
    ...(input.exemptionReason === undefined ? {} : { exemptionReason: input.exemptionReason }),
    lines,
    dividers,
  };
}

/** 🔒 Decision 9600: the two answers Tax Exempt Form takes. */
const EXEMPT_NONE = 'None';
const EXEMPT_ONE_TIME = 'OneTime';
const EXEMPT_CHOICES = [{ value: EXEMPT_NONE, label: t('po.form.exemptNone') }, { value: EXEMPT_ONE_TIME, label: t('po.form.exemptOneTime') }];
const EXEMPT_REASONS = ONE_TIME_EXEMPTION_REASONS.map((reason) => ({ value: reason, label: exemptionReasonLabel(reason) }));

/**
 * 🔒 Tax Exempt Form (decision 9600): None, or One-time Certificate with the state the ship-to's address names and a
 * reason opening on the Settings default. Where the application cannot fill the state's form the refusal stands under
 * the choice in the server's own sentence, and the save meets the same refusal.
 */
export function ExemptFormFields({ choice, reason, facts, onChoice, onReason }: {
  choice: string;
  reason: string;
  facts: PurchaseExemptionFacts | null;
  onChoice: (choice: string) => void;
  onReason: (reason: string) => void;
}) {
  const oneTime = choice === EXEMPT_ONE_TIME;
  const refusal = oneTime ? facts?.refusal ?? null : null;
  // 🔨 Each field is its own cell of the header's grid, so the three take the grid's spacing as the date pair does.
  return (
    <>
      <div data-po-field="exemptForm" data-po-exempt={oneTime ? (refusal ? 'refused' : 'one-time') : 'none'}>
        {/* The longer choice reads 20 characters, so the rule of decision 0583 says Large. */}
        <Select size={Size.Large} label={t('po.form.exemptForm')} value={choice} onChange={onChoice} options={EXEMPT_CHOICES} error={refusal} dataAttribute={{ 'data-po-field': 'exemptChoice' }} />
      </div>
      {oneTime && !refusal ? (
        <>
          <div data-po-exempt-part="state">
            <ModalField label={t('po.form.exemptState')}>
              <span data-po-exempt-state={facts?.stateCode ?? ''}>{facts?.stateName ? line(facts.stateName, `(${facts.stateCode})`) : t('po.form.exemptNeedsSite')}</span>
            </ModalField>
          </div>
          <div className={styles.wide} data-po-exempt-part="reason">
            <Select size={Size.Large} label={t('po.form.exemptReason')} value={reason} onChange={onReason} options={EXEMPT_REASONS} required dataAttribute={{ 'data-po-field': 'exemptReason' }}
              hint={facts?.formCode && facts.stateName ? fill(t('po.form.exemptHint'), { form: facts.formCode, state: facts.stateName }) : null} />
          </div>
        </>
      ) : null}
    </>
  );
}

/**
 * 🔒 The Purchase Order's edit mode (decision 0409), on the order's own page
 * rather than in a modal. The header fields run in the order the product owner
 * showed: the vendor, then the address block — our own Bill To beside the
 * delivery address as Locations or Customer, with the chosen destination and a
 * control to change it — then the date, the delivery date, the shipment
 * preference and the number. The lines follow on the Item Table's purchase
 * column set, and the sticky footer carries Save as Draft, Save and Send and
 * Cancel.
 *
 * 🔨 No field the record does not declare is drawn. Of the six header fields
 * the Zoho screen shows, the order carries the Date, the Delivery Date and the
 * Shipment Preference, and decision 0682 says why it carries none of the other
 * three. The Bill To is ours and is shown, never typed.
 */
export function PurchaseOrderForm({ orderId = null, apiBaseUrl = '' }: { orderId?: string | null; apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const vendorFromUrl = params.get('vendorId') ?? '';
  const salesOrderFromUrl = params.get('salesOrderId') ?? '';
  // 🔨 An absent Deal is null, never the empty string: the server writes what it is sent, and a uuid column refuses '' (found while building, 2026-09-15).
  const dealFromUrl = params.get('dealId') || null;

  const sites = useRows<{ id: string; label: string }>(`${PURCHASE_ORDERS_PATH}/receiving-sites`, apiBaseUrl);
  // 🔒 Our own billing address, read and never typed (T133; decision 0682).
  const billToRead = useApi<{ name: string; siteLabel: string | null; lines: string[] }>(`${PURCHASE_ORDERS_PATH}/bill-to`, apiBaseUrl);
  /* 🔨 An answer missing its lines reads as no billing site rather than crashing the form (a fixture that lacked the key did, found while building, 2026-09-24). */
  const billTo = billToRead.state.kind === 'ready' && Array.isArray(billToRead.state.data?.lines) ? billToRead.state.data : null;
  // A vendor named in the URL is resolved by id, so the picker opens on that vendor's own name.
  const namedVendor = useRows<{ id: string; displayName: string }>(vendorFromUrl ? `${PURCHASE_ORDERS_PATH}/vendors?id=${encodeURIComponent(vendorFromUrl)}` : null, apiBaseUrl);
  const stored = useApi<PurchaseOrderAnswer>(orderId ? `${PURCHASE_ORDERS_PATH}/${orderId}` : null, apiBaseUrl);

  const [vendorId, setVendorId] = useState(vendorFromUrl);
  const [vendorName, setVendorName] = useState('');
  const [method, setMethod] = useState<PurchaseFulfillmentMethodValue>(salesOrderFromUrl ? 'DropShip' : 'Stock');
  const [shipToSiteId, setShipToSiteId] = useState('');
  const [changing, setChanging] = useState(false);
  const [salesOrderId, setSalesOrderId] = useState(salesOrderFromUrl);
  const [salesOrderLabel, setSalesOrderLabel] = useState('');
  // 🔒 The order's own date (T114; decision 0681): a new order opens on today, a stored one on its own day.
  // 🔒 A new order opens on today in the viewer's own zone (decision 1030).
  const today = useDocumentToday();
  const [documentDate, setDocumentDate] = useState(today);
  // 🔒 The day the goods are wanted and how they travel (T134, T115; decision 0682).
  const [deliveryDate, setDeliveryDate] = useState('');
  const [shipmentPreference, setShipmentPreference] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  // 🔒 Decision 9600: Tax Exempt Form, None until a person chooses One-time Certificate; the reason opens on the Settings default.
  const [exemptChoice, setExemptChoice] = useState(EXEMPT_NONE);
  const [exemptReason, setExemptReason] = useState('');
  // 🔒 A fresh document opens on one empty line, so the table is typed into rather than found (decision 0447).
  const [drafts, setDrafts] = useState<ItemLineDraft[]>(() => [newLine()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The vendor's name arrives after the form opened; the state adjusts during the render that sees it.
  const vendorRow = namedVendor.state.kind === 'ready' ? namedVendor.state.data[0] ?? null : null;
  const [seenVendor, setSeenVendor] = useState<string | null>(null);
  if (vendorRow && seenVendor !== vendorRow.id) {
    setSeenVendor(vendorRow.id);
    setVendorId(vendorRow.id);
    setVendorName(vendorRow.displayName);
  }

  // The stored order fills the form when Edit reopens it, once and never over a later keystroke.
  const order = stored.state.kind === 'ready' ? stored.state.data : null;
  const [seenOrder, setSeenOrder] = useState<string | null>(null);
  if (order && seenOrder !== order.id) {
    setSeenOrder(order.id);
    setVendorId(order.vendorCompanyId);
    setVendorName(order.vendorName);
    setMethod(order.fulfillmentMethod);
    setShipToSiteId(order.shipToSiteId);
    // 🔨 A drop-ship order names the Sales Order it ships against, so Edit reopens holding it; without this the order could never be saved again.
    setSalesOrderId(order.salesOrderId ?? '');
    setSalesOrderLabel(order.salesOrderNumber ?? '');
    setDocumentDate(order.documentDate);
    setDeliveryDate(order.deliveryDate ?? '');
    setShipmentPreference(order.shipmentPreferenceId ?? '');
    setCustomerNotes(order.customerNotes ?? '');
    setExemptChoice(order.exemptionReason ? EXEMPT_ONE_TIME : EXEMPT_NONE);
    setExemptReason(order.exemptionReason ?? '');
    // 🔒 The stored group dividers stand among the lines where they were saved (decision 0650).
    setDrafts(withDividers(order.lines.map((row) => ({
      id: row.id,
      itemId: row.itemId,
      itemLabel: row.itemName ?? row.description,
      sku: row.sku,
      description: row.description,
      qty: row.quantity,
      unit: row.unitName,
      rateCents: row.unitPriceCents,
      // 🔨 Edit reopens on the account and the category the line was written with; re-defaulting them would undo a buyer's own pick (decision 0435).
      accountId: row.accountId,
      accountLabel: row.accountLabel,
      taxCategoryId: row.taxCategoryId,
      taxLabel: row.taxCategoryName,
      freeText: row.itemId === null,
    })), order.dividers));
  }

  const siteRows = sites.state.kind === 'ready' ? sites.state.data : [];
  // One site is the only choice, so it is taken; two or more leave the pick to the person.
  const chosenSite = shipToSiteId || (siteRows.length === 1 ? siteRows[0]!.id : '');
  const chosenSiteLabel = siteRows.find((s) => s.id === chosenSite)?.label ?? '';
  const picking = changing || !chosenSite;
  // 🔒 Decision 9600: the ship-to's state and the refusal a One-time Certificate meets there, read again as the ship-to moves.
  const exemptQuery = method === 'DropShip' ? (salesOrderId ? `salesOrderId=${encodeURIComponent(salesOrderId)}` : null) : (chosenSite ? `shipToSiteId=${encodeURIComponent(chosenSite)}` : null);
  const exemptRead = useApi<PurchaseExemptionFacts>(exemptQuery ? `${PURCHASE_ORDERS_PATH}/exemption-form?${exemptQuery}` : null, apiBaseUrl);
  const exemptFacts = exemptRead.state.kind === 'ready' ? exemptRead.state.data : null;
  const chooseExempt = (next: string) => {
    setExemptChoice(next);
    if (next === EXEMPT_ONE_TIME && !exemptReason) setExemptReason(exemptFacts?.defaultReason ?? ONE_TIME_EXEMPTION_REASONS[0]!);
  };

  const vendorSearch = async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PURCHASE_ORDERS_PATH}/vendors?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; displayName: string }[] };
    return body.rows.map((row) => ({ id: row.id, line1: row.displayName }));
  };

  // 🔒 The ACCOUNT and TAX pickers are the one pair every priced document reads (decisions 0097, 0435).
  const accountSearch = useMemo(() => postingAccountSearch(apiBaseUrl), [apiBaseUrl]);
  const taxSearch = useMemo(() => taxCategorySearch(apiBaseUrl), [apiBaseUrl]);

  const salesOrderSearch = async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PURCHASE_ORDERS_PATH}/sales-orders?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: SalesOrderRow[] };
    return body.rows.map((row) => ({ id: row.id, line1: row.salesOrderNumber, line2: line(row.companyName, row.hasShipTo ? null : `(${t('po.new.noShipTo')})`) }));
  };
  const { prices, pickerProps } = useItemPicker(apiBaseUrl, 'purchase');

  const save = async (then: 'view' | 'send'): Promise<void> => {
    if (!vendorId) return setError(t('po.new.needsVendor'));
    if (method === 'Stock' && !chosenSite) return setError(t('po.new.needsSite'));
    if (method === 'DropShip' && !salesOrderId) return setError(t('po.new.needsSalesOrder'));
    if (hasUnnamedDivider(drafts)) return setError(t('item.dividerNeedsName'));
    const body = requestFrom({ vendorCompanyId: vendorId, method, shipToSiteId: chosenSite, salesOrderId, dealId: order?.dealId ?? dealFromUrl, documentDate, deliveryDate, shipmentPreferenceId: shipmentPreference, customerNotes, exemptionReason: exemptChoice === EXEMPT_ONE_TIME ? exemptReason || exemptFacts?.defaultReason || null : null, drafts });
    if (body.lines.length === 0) return setError(t('po.new.needsLines'));
    setBusy(true);
    const answer = orderId
      ? await put<PurchaseOrderAnswer>(`${PURCHASE_ORDERS_PATH}/${orderId}`, body, apiBaseUrl)
      : await post<PurchaseOrderAnswer>(PURCHASE_ORDERS_PATH, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    // 🔨 Save and Send saves first and opens the send on the saved page, so a send that fails leaves a saved order.
    navigate(`/purchase-orders/${answer.data.id}${then === 'send' ? '?act=send' : ''}`);
  };

  return (
    <>
    <DocumentEditPage
      title={order ? order.purchaseOrderNumber : newDocumentTitle('PurchaseOrder')}
      subtitle={documentName('PurchaseOrder')}
      outwardLabel={t('document.edit.saveAndSend')}
      busy={busy}
      error={error}
      onDismissError={() => setError(null)}
      onSaveDraft={() => void save('view')}
      onSaveAndSend={() => void save('send')}
      onCancel={() => navigate(orderId ? `/purchase-orders/${orderId}` : '/purchase-orders')}
      header={
        <>
          {/* 🔨 Opened from a vendor's own page the picker is locked to that company (piece 22); the order is for the vendor whose page raised it. */}
          <div data-po-field="vendor">
            <Dropdown size={Size.Medium} label={t('po.new.vendor')} entityLabel={t('po.new.vendorEntity')} search={vendorSearch} minChars={0} value={vendorName} onSelect={(row) => { setVendorId(row.id); setVendorName(row.line1); }} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'vendor' }, (row) => { setVendorId(row.id); setVendorName(row.line1); })} disabled={vendorFromUrl !== ''} required />
          </div>
          <div className={styles.wide} data-po-field="billTo">
            <ModalField label={t('po.form.billTo')}>
              {billTo && billTo.lines.length > 0 ? (
                <span className={styles.destinationLines} data-po-bill-to={billTo.siteLabel ?? ''}>
                  <span className={styles.destinationLine}>{billTo.name}</span>
                  {billTo.lines.map((row) => <span key={row} className={styles.destinationLine}>{row}</span>)}
                </span>
              ) : (
                <span className={styles.hint} data-po-bill-to="">{billToRead.state.kind === 'ready' ? t('po.form.billToMissing') : ''}</span>
              )}
            </ModalField>
            <p className={styles.hint}>{t('po.form.billToHint')}</p>
          </div>
          <div className={styles.wide} data-po-field="delivery">
            <ModalField label={t('po.form.deliveryAddress')} required>
              <div className={styles.choice} role="radiogroup" aria-label={t('po.form.deliveryAddress')}>
                <label className={styles.radio}>
                  <input type="radio" name="poDestination" value="Stock" checked={method === 'Stock'} onChange={() => { setMethod('Stock'); setChanging(false); }} data-po-destination="Stock" />
                  <span>{t('po.form.destinationLocations')}</span>
                </label>
                <label className={styles.radio}>
                  <input type="radio" name="poDestination" value="DropShip" checked={method === 'DropShip'} onChange={() => { setMethod('DropShip'); setChanging(false); }} data-po-destination="Customer" />
                  <span>{t('po.form.destinationCustomer')}</span>
                </label>
              </div>
            </ModalField>
            {method === 'Stock' ? (
              picking ? (
                <Select size={Size.Medium}
                  label={t('po.new.shipTo')}
                  value={chosenSite}
                  onChange={(next) => { setShipToSiteId(next); setChanging(false); }}
                  placeholder={t('customFieldsModal.none')}
                  options={siteRows.map((site) => ({ value: site.id, label: site.label }))}
                  dataAttribute={{ 'data-po-field': 'shipTo' }}
                  required
                />
              ) : (
                <div className={styles.destination} data-po-destination-block="Stock">
                  <span className={styles.destinationLines}>
                    <span className={styles.destinationLine}>{chosenSiteLabel}</span>
                  </span>
                  <Button size={Size.Small} text={[t('po.form.changeDestination')]} actions={[() => setChanging(true)]} />
                </div>
              )
            ) : (
              <div data-po-field="salesOrder">
                <Dropdown size={Size.Medium} label={t('po.new.salesOrder')} entityLabel={t('po.new.salesOrderEntity')} search={salesOrderSearch} minChars={0} value={salesOrderLabel} onSelect={(row) => { setSalesOrderId(row.id); setSalesOrderLabel(line(row.line1, '·', row.line2 ?? '')); }} onAddNew={() => addNew.open({ kind: 'document', documentType: 'SalesOrder' }, (row) => { setSalesOrderId(row.id); setSalesOrderLabel(line(row.line1, '·', row.line2 ?? '')); })} required />
                <p className={styles.hint}>{t('po.new.salesOrderHint')}</p>
              </div>
            )}
          </div>
          <div data-po-field="date">
            <DatePicker size={Size.Small} label={t('document.edit.date')} value={documentDate} onChange={setDocumentDate} mode="date" name="purchaseOrderDate" />
          </div>
          <div data-po-field="deliveryDate">
            <DatePicker size={Size.Small} label={t('po.form.deliveryDate')} value={deliveryDate} onChange={setDeliveryDate} mode="date" name="purchaseOrderDeliveryDate" />
          </div>
          <ShipmentPreferencePicker value={shipmentPreference} onChange={setShipmentPreference} apiBaseUrl={apiBaseUrl} name="purchaseOrder" />
          <ExemptFormFields choice={exemptChoice} reason={exemptReason} facts={exemptFacts} onChoice={chooseExempt} onReason={setExemptReason} />
          <ModalField label={t('po.form.number')}>
            <span data-po-field="number">{order ? order.purchaseOrderNumber : t('po.form.numberOnSave')}</span>
          </ModalField>
          <CustomerNotesBox value={customerNotes} onChange={setCustomerNotes} party="vendor" name="purchaseOrder" />
        </>
      }
      lines={
        <ItemTable
          documentType="PurchaseOrder"
          columns={['item', 'qty', 'unit', 'rate', 'account', 'tax', 'amount']}
          lines={drafts}
          onChange={setDrafts}
          discountLevel="Footer"
          {...pickerProps()}
          accountSearch={accountSearch}
          taxSearch={taxSearch}
          referenceCentsOf={(draft) => (draft.itemId ? prices.current.get(draft.itemId) ?? null : null)}
        />
      }
    />
    {addNew.host}
    </>
  );
}
